import { Loader, ModalParts } from 'BreetComponents';
import { alwaysRefetchApiQueryOptions } from 'BreetHelpers';
import { useFormStage, useWithdrawUsdRequestContext } from 'BreetHooks';
import { selectSaveWalletAddresses, useAppSelector, useGetSavedWalletAddressesQuery } from 'BreetRedux';
import { type FC, useEffect } from 'react';

import { Form } from './components/form/Form';
import { WithdrawUsdRequestProvider } from './components/FormProvider';
import { WithdrawRequestUsdFormStageContext } from './components/FormStageContext';
import SendCryptoTransactionDetails from './components/SendCryptoTransactionDetails';
import {
	DialogWithdrawUsdAddWalletAddress,
	DialogWithdrawUsdScanWalletAddress,
	DialogWithdrawUsdWalletAddressesManage,
	DialogWithdrawUsdWalletAddressesSaved,
} from './components/walletAddresses';
import { requestPaymentUsdStageTitle, type WITHDRAWREQUESTUSDSTAGETYPE } from './DialogWithdrawUsdRequest.helpers';

// Inner component — lives inside WithdrawUsdRequestProvider so it can access the context
const DialogWithdrawUsdRequestContent = () => {
	const { isFetching: isFetchingSavedWalletAddress } = useGetSavedWalletAddressesQuery(undefined, alwaysRefetchApiQueryOptions);

	const { isSuccess } = useAppSelector(selectSaveWalletAddresses);

	const { formDataContextProps } = useWithdrawUsdRequestContext();

	const formStage = useFormStage<WITHDRAWREQUESTUSDSTAGETYPE>(
		'withdraw-funds',
		[
			'withdraw-funds',
			'add-wallet-address',
			'saved-wallet-address',
			'manage-wallet-address',
			'scan-wallet-address',
			'crypto-transaction-details',
		],
		requestPaymentUsdStageTitle
	);

	useEffect(() => {
		if (isSuccess) {
			formStage.navigator.goTo('withdraw-funds')();
			formDataContextProps.hookFormProps.trigger().catch(console.error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccess]);

	const { isStageActive, stageTitle, navigator } = formStage;

	const stageComponents: Record<WITHDRAWREQUESTUSDSTAGETYPE, FC> = {
		'withdraw-funds': Form,
		'add-wallet-address': DialogWithdrawUsdAddWalletAddress,
		'saved-wallet-address': DialogWithdrawUsdWalletAddressesSaved,
		'manage-wallet-address': DialogWithdrawUsdWalletAddressesManage,
		'scan-wallet-address': DialogWithdrawUsdScanWalletAddress,
		'crypto-transaction-details': SendCryptoTransactionDetails,
	};

	return (
		<WithdrawRequestUsdFormStageContext.Provider value={formStage}>
			{isFetchingSavedWalletAddress ? (
				<Loader />
			) : (
				<ModalParts.Wrapper
					title={stageTitle}
					rightSheet
					root={requestPaymentUsdStageTitle['withdraw-funds']}
					handleBackLink={navigator.historyBack}
				>
					{Object.entries(stageComponents).map(([stage, Component]) => (
						<ModalParts.Stage
							hide={!isStageActive(stage as WITHDRAWREQUESTUSDSTAGETYPE)}
							key={stage}
						>
							<Component />
						</ModalParts.Stage>
					))}
				</ModalParts.Wrapper>
			)}
		</WithdrawRequestUsdFormStageContext.Provider>
	);
};

const DialogWithdrawUsdRequest = () => (
	<WithdrawUsdRequestProvider>
		<DialogWithdrawUsdRequestContent />
	</WithdrawUsdRequestProvider>
);

export default DialogWithdrawUsdRequest;
